import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Input, Row, Space } from 'antd';
import { useEffect, useState } from 'react';
import { getZipCodes } from '../../api';
import { components, paths } from '../../api/schema';
import DataTable, {
  ColumnsType,
  DataTableFilterCallback,
  DataTableOrderCallback,
  serializeFilters,
  serializeOrders,
} from '../../shared/dataTable/DataTable';
import useDebounce from '../../shared/hooks/useDebounce';
import PanelSkeleton from '../../shared/skeletons/PanelSkeleton';
import CreateZipCode from './CreateZipCode';
import { DeleteZipCode } from './zipCodePanelActions/DeleteAction';
import { EditZipCode } from './zipCodePanelActions/EditAction';

const zipCodesTableColumns: ColumnsType<components['schemas']['ZipcodeCondensed']> = [
  {
    title: 'C.P.',
    dataIndex: 'zipCode',
    key: 'zipCode',
    columnType: 'text',
  },
  {
    title: 'Colonia',
    dataIndex: 'district',
    key: 'district',
    columnType: 'text',
  },

  {
    title: 'Municipio',
    dataIndex: 'municipio',
    key: 'municipio',
    columnType: 'text',
  },
  {
    title: 'Estado',
    dataIndex: 'state',
    key: 'state',
    columnType: 'text',
  },
  {
    title: 'Precio',
    dataIndex: 'price',
    key: 'price',
    columnType: 'text',
  },
  {
    title: 'Zona',
    dataIndex: 'zoneName',
    key: 'zoneName',
    columnType: 'text',
  },
];
const PAGE_SIZE = 50;

export default function ZipCodePanel() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce<string>(search, 300);
  const [params, setParams] = useState<paths['/zipcode/list']['get']['parameters']['query']>({
    search: undefined,
    limit: PAGE_SIZE,
    page: 1,
    showDisabled: false,
    order: JSON.stringify([]),
    filter: JSON.stringify([]),
  });
  const { isLoading, isError, data, isFetching } = useQuery({
    queryKey: ['getZipCodes', params],
    queryFn: () => getZipCodes(params),
  });

  useEffect(() => {
    setParams((state) => ({ ...state, search: debouncedSearch }));
  }, [debouncedSearch]);

  const handleFiltering = (filter: DataTableFilterCallback) => {
    setParams((state) => ({ ...state, filter: serializeFilters(params.filter, filter) }));
  };

  const handleOrdering = (order: DataTableOrderCallback) => {
    setParams((state) => ({ ...state, order: serializeOrders(params.order, order) }));
  };

  const handlePageChange = (page: number) => {
    setParams((state) => ({ ...state, page }));
  };

  return (
    <Row>
      <Col span={24}>
        <Space size={'middle'}>
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            Crear Código postal
          </Button>
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            suffix={<SearchOutlined />}
          />
        </Space>
      </Col>
      {isLoading || isError || isFetching ? (
        <PanelSkeleton />
      ) : (
        <>
          <Col span={24} style={{ marginTop: '10px' }}>
            <DataTable<components['schemas']['ZipcodeCondensed']>
              handleFilters={handleFiltering}
              handleOrder={handleOrdering}
              handlePageChange={handlePageChange}
              columns={zipCodesTableColumns}
              results={data.results}
              activeFilters={params.filter}
              activeOrders={params.order}
              paginationData={{
                pageSize: PAGE_SIZE,
                count: data.count,
                currentPage: data.currentPage,
              }}
              actions={[EditZipCode, DeleteZipCode]}
            />
          </Col>

          <CreateZipCode
            open={createModalOpen}
            onCancel={() => setCreateModalOpen(false)}
            onCreate={() => setCreateModalOpen(false)}
          />
        </>
      )}
    </Row>
  );
}
